
import React, { useState } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Server, BarChart3 } from 'lucide-react';

const AlertsTrend = ({ alerts, cardClasses, isDarkMode, dateFilter }) => {
  // Estado para controlar vista: 'total' o 'byCloud'
  const [viewMode, setViewMode] = useState('total');
  
  // Estado para controlar qué nubes están visibles
  const [visibleClouds, setVisibleClouds] = useState({
    AWS: true,
    Azure: true,
    GCP: true,
    Huawei: true
  });

  // Toggle de visibilidad de una nube
  const toggleCloud = (cloudName) => {
    setVisibleClouds(prev => ({
      ...prev,
      [cloudName]: !prev[cloudName]
    }));
  };
  
  // Colores por proveedor de nube
  const cloudColors = {
    AWS: '#FF9900',      // Naranja AWS
    Azure: '#0078D4',    // Azul Azure
    GCP: '#34A853',      // Verde Google
    Huawei: '#FF0000'    // Rojo Huawei
  };
  // Función para normalizar fechas a la zona horaria local
  const normalizeDate = (dateString) => {
    const date = new Date(dateString);
    // Si la fecha viene en UTC, ajustarla a la zona horaria local
    const localDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
    return localDate.toISOString().split('T')[0]; // YYYY-MM-DD format
  };

  // Función para normalizar nombres de nubes
  const normalizeCloudName = (cloudName) => {
    if (!cloudName) return 'Desconocido';
    const normalized = cloudName.toLowerCase();
    // Mapear a nombres estándar
    if (normalized.includes('aws') || normalized.includes('amazon')) return 'AWS';
    if (normalized.includes('azure') || normalized.includes('microsoft')) return 'Azure';
    if (normalized.includes('gcp') || normalized.includes('google')) return 'GCP';
    if (normalized.includes('huawei')) return 'Huawei';
    // Si no coincide, capitalizar primera letra
    return cloudName.charAt(0).toUpperCase() + cloudName.slice(1).toLowerCase();
  };

  // Agrupar alertas por fecha normalizada
  const alertTrendData = alerts.reduce((acc, alert) => {
    const fecha = normalizeDate(alert.Fecha);
    const nube = normalizeCloudName(alert.Nube);
    const existingDate = acc.find(item => item.date === fecha);
    
    if (existingDate) {
      existingDate.cantidad += alert.Cantidad || 0;
      // Agregar por nube
      existingDate[nube] = (existingDate[nube] || 0) + (alert.Cantidad || 0);
    } else {
      const newEntry = { 
        date: fecha, 
        cantidad: alert.Cantidad || 0,
        formatted: new Date(fecha + 'T12:00:00').toLocaleDateString('es-MX', { 
          month: 'short', 
          day: 'numeric' 
        }),
        AWS: 0,
        Azure: 0,
        GCP: 0,
        Huawei: 0
      };
      newEntry[nube] = alert.Cantidad || 0;
      acc.push(newEntry);
    }
    return acc;
  }, []).sort((a, b) => new Date(a.date) - new Date(b.date));
  
  // Obtener nubes disponibles en los datos (normalizadas)
  const availableClouds = [...new Set(alerts.map(a => normalizeCloudName(a.Nube)).filter(Boolean))].sort();
  
  return (
    <div className={`${cardClasses} p-6 rounded-2xl mb-8`}>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-xl font-bold flex items-center">
          <Server className="w-6 h-6 mr-2 text-red-400" />
          Tendencia de Alertas ({dateFilter})
        </h3>
        
        {/* Switch para cambiar vista */}
        <div className="flex items-center space-x-2 bg-white/5 rounded-lg p-1">
          <button
            onClick={() => setViewMode('total')}
            className={`flex items-center space-x-2 px-3 py-1.5 rounded-md transition-all duration-200 ${
              viewMode === 'total'
                ? 'bg-red-500/20 text-red-300'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <BarChart3 className="w-4 h-4" />
            <span className="text-xs font-medium">Total</span>
          </button>
          <button
            onClick={() => setViewMode('byCloud')}
            className={`flex items-center space-x-2 px-3 py-1.5 rounded-md transition-all duration-200 ${
              viewMode === 'byCloud'
                ? 'bg-blue-500/20 text-blue-300'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <Server className="w-4 h-4" />
            <span className="text-xs font-medium">Por Nube</span>
          </button>
        </div>
      </div>
      
      <ResponsiveContainer width="100%" height={250}>
        <AreaChart data={alertTrendData}>
          <defs>
            {/* Gradiente para vista total */}
            <linearGradient id="alertGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#EF4444" stopOpacity={0.8}/>
              <stop offset="95%" stopColor="#EF4444" stopOpacity={0.1}/>
            </linearGradient>
            
            {/* Gradientes dinámicos por nube */}
            {availableClouds.map(cloud => {
              const color = cloudColors[cloud] || '#8B5CF6';
              return (
                <linearGradient key={cloud} id={`${cloud.toLowerCase()}Gradient`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={color} stopOpacity={0.8}/>
                  <stop offset="95%" stopColor={color} stopOpacity={0.1}/>
                </linearGradient>
              );
            })}
          </defs>
          
          <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#374151' : '#E5E7EB'} />
          <XAxis 
            dataKey="formatted" 
            stroke={isDarkMode ? '#9CA3AF' : '#6B7280'}
            fontSize={11}
            axisLine={false}
            tickLine={false}
          />
          <YAxis 
            stroke={isDarkMode ? '#9CA3AF' : '#6B7280'}
            fontSize={11}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip 
            contentStyle={{
              backgroundColor: isDarkMode ? '#1F2937' : 'white',
              border: 'none',
              borderRadius: '12px',
              boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
              fontSize: '13px'
            }}
            formatter={(value, name) => {
              if (viewMode === 'total') {
                return [value, 'Alertas'];
              }
              return [value, name];
            }}
            labelFormatter={(label) => `${label}`}
          />
          
          {/* Vista Total */}
          {viewMode === 'total' && (
            <Area 
              type="monotone" 
              dataKey="cantidad" 
              stroke="#EF4444" 
              strokeWidth={2}
              fillOpacity={1}
              fill="url(#alertGradient)"
              name="Total Alertas"
            />
          )}
          
          {/* Vista Por Nube - Renderizar dinámicamente todas las nubes disponibles */}
          {viewMode === 'byCloud' && availableClouds.map(cloud => {
            // Verificar si esta nube está visible
            const isVisible = visibleClouds[cloud] !== undefined ? visibleClouds[cloud] : true;
            if (!isVisible) return null;
            
            // Obtener color y gradiente
            const color = cloudColors[cloud] || '#8B5CF6'; // Morado por defecto
            const gradientId = `${cloud.toLowerCase()}Gradient`;
            
            return (
              <Area 
                key={cloud}
                type="monotone" 
                dataKey={cloud} 
                stroke={color}
                strokeWidth={2}
                fillOpacity={1}
                fill={`url(#${gradientId})`}
                name={cloud}
              />
            );
          })}
        </AreaChart>
      </ResponsiveContainer>
      
      {/* Leyenda interactiva solo en vista por nube */}
      {viewMode === 'byCloud' && availableClouds.length > 0 && (
        <div className="flex justify-center mt-3 space-x-6">
          {availableClouds.map(cloud => (
            <button
              key={cloud}
              onClick={() => toggleCloud(cloud)}
              className={`flex items-center space-x-2 px-3 py-1 rounded-lg transition-all duration-200 ${
                visibleClouds[cloud] 
                  ? 'opacity-100 hover:bg-white/10' 
                  : 'opacity-40 hover:opacity-60'
              }`}
              title={visibleClouds[cloud] ? 'Click para ocultar' : 'Click para mostrar'}
            >
              <div 
                className={`w-3 h-2 rounded-sm ${visibleClouds[cloud] ? 'opacity-80' : 'opacity-30'}`}
                style={{ backgroundColor: cloudColors[cloud] }}
              ></div>
              <span className="text-xs font-medium">{cloud}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default AlertsTrend;